/** @jsxRuntime classic */
/** @jsx jsx */

import React, { useState } from 'react';
import { jsx } from '@emotion/react';
import dayjs from 'dayjs';
import useInterval from '../../hooks/useInterval';
import { hourBoxLine2 } from '../styles';
import { useMiddlewareContext } from '../../hooks/useMiddlewareContext';
import { DateTimeFormats, isSameDate } from '@bondsports/date-time';

const INTERVAL = 1000 as const; // 1 second

/**
 * Get the current hour.
 * @returns {number} The current hour.
 */
const getCurrentHour = (): number => {
	return Number(dayjs().format('HH'));
};

/**
 * Get the current hour in milliseconds.
 * @param hour {number} The hour to set.
 * @returns {number} The current hour in milliseconds.
 */
const getHourTime = (hour: number): number => {
	return dayjs().hour(hour).valueOf();
};

type TimeLineProps = {
	width?: number;
};

export const CurrentTimeLine: React.FC<TimeLineProps> = React.memo(({ width }) => {
	const { getTopAndHeight, options } = useMiddlewareContext();

	const nowHours: number = getCurrentHour();
	const utcHours: number = getCurrentHour();
	const diff: number = nowHours - utcHours;

	const start: number = getHourTime(nowHours + diff);

	const top: number = getTopAndHeight(start, 0).top;

	const [marginTop, setMarginTop] = useState(top);

	useInterval(() => {
		const now: number = getHourTime(nowHours + diff);
		const top: number = getTopAndHeight(now, 0).top;

		setMarginTop(top);
	}, INTERVAL);

	// micro optimization - cpu's branch prediction necessitates the if statement to contain the hot path.
	// this is a valid spot as the component renders every second.
	if (isSameDate(dayjs(options?.date, DateTimeFormats.YYYY_MM_DD), Date.now(), 'date')) {
		return <div css={hourBoxLine2(width, marginTop)} data-aid="CurrentTimeLine-daily" key="CurrentTimeLine-daily" />;
	}

	return null;
});
